import React, { useState, useRef } from 'react';
import { 
  Plus, Package, Image, Trash2, Edit, ChevronDown, ChevronUp,
  DollarSign, Tag, Sparkles, Search, ExternalLink, ShoppingCart, Link2, Globe
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ProductProfile } from '../components/ProductProfile';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { EmptyState } from '../components/EmptyState';

const marketplaceConfig = {
  amazon: { name: 'Amazon', color: '#FF9900', icon: '🛒' },
  mercadolibre: { name: 'MercadoLibre', color: '#FFE600', icon: '🤝' },
  shopify: { name: 'Shopify', color: '#96BF48', icon: '🛍' },
  ebay: { name: 'eBay', color: '#E53238', icon: '🏷' },
  etsy: { name: 'Etsy', color: '#F1641E', icon: '🎨' },
  woocommerce: { name: 'WooCommerce', color: '#96588A', icon: '🔌' },
  other: { name: 'Otro', color: '#6B7280', icon: '📦' }
};

const Products = () => {
  const { products, campaigns, createProduct, updateProduct, deleteProduct, generateImage } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [generating, setGenerating] = useState(false);
  const [formTab, setFormTab] = useState('manual');
  const [profileProduct, setProfileProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', category: '', campaign_id: '',
    price: '', cost: '', brand_guidelines: '', image_style: '', images: [],
    marketplace_source: '', marketplace_url: '', marketplace_sku: '', stock: ''
  });
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setFormData({ name: '', description: '', category: '', campaign_id: '', price: '', cost: '', brand_guidelines: '', image_style: '', images: [], marketplace_source: '', marketplace_url: '', marketplace_sku: '', stock: '' });
    setEditingProduct(null);
    setFormTab('manual');
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name || '', description: product.description || '', category: product.category || '',
      campaign_id: product.campaign_id || '', price: product.price || '', cost: product.cost || '',
      brand_guidelines: product.brand_guidelines || '', image_style: product.image_style || '',
      images: product.images || [], marketplace_source: product.marketplace_source || '',
      marketplace_url: product.marketplace_url || '', marketplace_sku: product.marketplace_sku || '',
      stock: product.stock || ''
    });
    setEditingProduct(product);
    setFormTab(product.marketplace_source ? 'marketplace' : 'manual');
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.description.trim()) return;
    const data = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null,
      cost: formData.cost ? parseFloat(formData.cost) : null,
      stock: formData.stock ? parseInt(formData.stock) : null,
      campaign_id: formData.campaign_id || null,
      marketplace_source: formData.marketplace_source || null,
      marketplace_url: formData.marketplace_url || null,
      marketplace_sku: formData.marketplace_sku || null
    };
    if (editingProduct) await updateProduct(editingProduct.id, data);
    else await createProduct(data);
    resetForm(); setShowForm(false);
    toast.success(editingProduct ? 'Producto actualizado' : 'Producto creado');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setFormData(prev => ({ ...prev, images: [...prev.images, reader.result] }));
    reader.readAsDataURL(file);
  };

  const handleGenerateImage = async () => {
    if (!formData.name) return;
    setGenerating(true);
    try {
      const result = await generateImage(formData.name, formData.description, formData.image_style);
      if (result?.image) setFormData(prev => ({ ...prev, images: [...prev.images, result.image] }));
      toast.success('Imagen generada');
    } catch { toast.error('Error al generar imagen'); }
    finally { setGenerating(false); }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCampaign = selectedCampaign === 'all' || p.campaign_id === selectedCampaign;
    return matchesSearch && matchesCampaign;
  });

  const getMargin = (price, cost) => (!price || !cost) ? null : ((price - cost) / price * 100).toFixed(1);

  return (
    <div data-testid="products-page" className="min-h-screen">
      <Header title="Productos" subtitle="Catálogo y productos de marketplace">
        <Button data-testid="new-product-btn" onClick={() => { resetForm(); setShowForm(true); }} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="w-4 h-4" /> Nuevo Producto
        </Button>
      </Header>

      <div className="p-6 space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="products-search" placeholder="Buscar productos..." className="pl-9 bg-muted border-none" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant={selectedCampaign === 'all' ? 'secondary' : 'outline'} size="sm" onClick={() => setSelectedCampaign('all')}>Todos ({products.length})</Button>
            {campaigns.map(camp => {
              const count = products.filter(p => p.campaign_id === camp.id).length;
              return count > 0 ? (
                <Button key={camp.id} variant={selectedCampaign === camp.id ? 'secondary' : 'outline'} size="sm" onClick={() => setSelectedCampaign(camp.id)}>
                  <span className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: camp.color }} />
                  {camp.name} ({count})
                </Button>
              ) : null;
            })}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => {
            const isExpanded = expandedProduct === product.id;
            const margin = getMargin(product.price, product.cost);
            const campaign = campaigns.find(c => c.id === product.campaign_id);
            const mpConfig = product.marketplace_source ? marketplaceConfig[product.marketplace_source] : null;

            return (
              <Card key={product.id} data-testid={`product-card-${product.id}`} className="glass-card overflow-hidden transition-all duration-300">
                {product.images?.[0] && <div className="h-40 overflow-hidden"><img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" /></div>}
                <CardContent className="p-4">
                  <div className="cursor-pointer" onClick={() => setExpandedProduct(isExpanded ? null : product.id)}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold cursor-pointer hover:text-brand-deep dark:text-brand-deep dark:text-brand-green transition-colors" onClick={(e) => { e.stopPropagation(); setProfileProduct(product); }}>{product.name}</h3>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {product.price && <Badge variant="outline" className="text-emerald-700 dark:text-emerald-400 border-emerald-500">${product.price}</Badge>}
                      {margin && <Badge variant="outline" className="text-xs">Margen: {margin}%</Badge>}
                      {campaign && <Badge variant="outline" className="text-xs" style={{ borderColor: campaign.color, color: campaign.color }}>{campaign.name}</Badge>}
                      {mpConfig && <Badge className="text-xs" style={{ backgroundColor: `${mpConfig.color}20`, color: mpConfig.color }}>{mpConfig.icon} {mpConfig.name}</Badge>}
                      {product.stock != null && <Badge variant="outline" className="text-xs">Stock: {product.stock}</Badge>}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3 animate-expand-in">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {product.category && <div className="flex items-center gap-2"><Tag className="w-4 h-4 text-muted-foreground" /><span>Cat: {product.category}</span></div>}
                        {product.cost && <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-muted-foreground" /><span>Costo: ${product.cost}</span></div>}
                      </div>
                      {product.marketplace_url && (
                        <a href={product.marketplace_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline">
                          <ExternalLink className="w-4 h-4" /> Ver en {mpConfig?.name || 'marketplace'}
                        </a>
                      )}
                      {product.marketplace_sku && <p className="text-xs text-muted-foreground">SKU: {product.marketplace_sku}</p>}
                      {product.brand_guidelines && <div className="text-sm"><span className="text-muted-foreground">Guía de marca:</span><p className="mt-1">{product.brand_guidelines}</p></div>}
                      {product.images?.length > 1 && <div className="grid grid-cols-3 gap-2">{product.images.slice(1).map((img, idx) => <img key={`img-${idx}-${img?.slice(-10)}`} src={img} alt="" className="w-full h-20 object-cover rounded-lg" />)}</div>}
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(product)}><Edit className="w-4 h-4 mr-1" /> Editar</Button>
                        <Button variant="destructive" size="sm" onClick={() => { deleteProduct(product.id); toast.success('Producto eliminado'); }}><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredProducts.length === 0 && (
          <Card className="glass-card border-dashed"><CardContent className="py-12 text-center">
            <EmptyState
              mood="empty"
              title="Sin productos"
              description={selectedCampaign !== 'all' ? 'Esta campaña todavía no tiene productos. Agrega uno para que Noah lo ofrezca.' : 'Agrega tu primer producto y Noah podrá venderlo en los chats.'}
              testId="products-empty"
            />
          </CardContent></Card>
        )}
      </div>

      {/* Product Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); setShowForm(open); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle></DialogHeader>
          
          <Tabs value={formTab} onValueChange={setFormTab} className="mt-2">
            <TabsList className="w-full">
              <TabsTrigger value="manual" className="flex-1"><Package className="w-4 h-4 mr-1" /> Manual</TabsTrigger>
              <TabsTrigger value="marketplace" className="flex-1"><ShoppingCart className="w-4 h-4 mr-1" /> Marketplace</TabsTrigger>
            </TabsList>
            
            <TabsContent value="marketplace" className="space-y-4 mt-4">
              <div className="p-4 bg-muted/30 rounded-lg border border-dashed">
                <div className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-primary" /><span className="text-sm font-medium">Importar desde Marketplace</span></div>
                <p className="text-xs text-muted-foreground">Conecta un producto desde una plataforma externa</p>
              </div>
              <div><Label>Plataforma</Label>
                <Select value={formData.marketplace_source} onValueChange={v => setFormData({...formData, marketplace_source: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar plataforma" /></SelectTrigger>
                  <SelectContent>{Object.entries(marketplaceConfig).map(([key, cfg]) => <SelectItem key={key} value={key}>{cfg.icon} {cfg.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>URL del producto</Label><Input value={formData.marketplace_url} onChange={(e) => setFormData({...formData, marketplace_url: e.target.value})} placeholder="https://..." /></div>
              <div><Label>SKU / ID del producto</Label><Input value={formData.marketplace_sku} onChange={(e) => setFormData({...formData, marketplace_sku: e.target.value})} placeholder="SKU-001" /></div>
            </TabsContent>
          </Tabs>

          <div className="space-y-4 pt-2">
            <div><Label>Nombre *</Label><Input data-testid="product-name-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Nombre del producto" /></div>
            <div><Label>Descripción *</Label><Textarea data-testid="product-desc-input" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Describe el producto..." rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Categoría</Label><Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} placeholder="Ej: Tecnología" /></div>
              <div><Label>Campaña</Label>
                <Select value={formData.campaign_id || "none"} onValueChange={v => setFormData({...formData, campaign_id: v === "none" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Sin campaña" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Sin campaña</SelectItem>{campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Precio ($)</Label><Input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} placeholder="0.00" /></div>
              <div><Label>Costo ($)</Label><Input type="number" value={formData.cost} onChange={(e) => setFormData({...formData, cost: e.target.value})} placeholder="0.00" /></div>
              <div><Label>Stock</Label><Input type="number" value={formData.stock} onChange={(e) => setFormData({...formData, stock: e.target.value})} placeholder="0" /></div>
            </div>
            <div><Label>Estilo de Imagen para IA</Label><Input value={formData.image_style} onChange={(e) => setFormData({...formData, image_style: e.target.value})} placeholder="Ej: Minimalista, profesional" /></div>
            <div>
              <Label>Imágenes</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {formData.images.map((img, idx) => (<div key={`edit-img-${idx}`} className="relative w-20 h-20"><img src={img} alt="" className="w-full h-full object-cover rounded-lg" /><button className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center text-xs" onClick={() => setFormData(prev => ({...prev, images: prev.images.filter((_, i) => i !== idx)}))}>×</button></div>))}
                <button className="w-20 h-20 border-2 border-dashed border-border rounded-lg flex items-center justify-center text-muted-foreground hover:border-primary" onClick={() => fileInputRef.current?.click()}><Image className="w-6 h-6" /></button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </div>
              <Button variant="outline" size="sm" className="mt-2 gap-2" onClick={handleGenerateImage} disabled={generating || !formData.name}><Sparkles className="w-4 h-4" />{generating ? 'Generando...' : 'Generar con IA'}</Button>
            </div>
            <Button data-testid="save-product-btn" className="w-full bg-primary" onClick={handleSubmit} disabled={!formData.name.trim() || !formData.description.trim()}>{editingProduct ? 'Guardar' : 'Crear Producto'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Profile Modal */}
      {profileProduct && (
        <ProductProfile
          product={profileProduct}
          onClose={() => setProfileProduct(null)}
        />
      )}
    </div>
  );
};

export default Products;
